import { FluzElementsError } from "./errors.js";

// Statically replaced by tsup at build time (see tsup.config.ts's `env`
// option). Checked inline at the call site below, not through a hoisted
// module-level const -- esbuild's minifier only folds away a `"false" ===
// "true" && expr` short-circuit (dropping the `isLocalhostOrigin` call and,
// once unreferenced, the function itself) when the check sits directly in
// the expression; routing it through an intermediate variable defeats that
// and leaves the escape hatch's code physically present, just unreachable.
// Declared ambiently because this package otherwise targets the browser
// (tsconfig lib is ES2022+DOM, no @types/node).
declare const process: { env: { FLUZ_ALLOW_LOCALHOST_FRAME_HOST?: string } };

export const DEFAULT_FRAME_HOST_ORIGIN = "https://secure.fluz.app";

const ALLOWED_FRAME_HOST_ORIGINS = new Set<string>(["https://secure.fluz.app", "https://staging.secure.fluz.app"]);

function isLocalhostOrigin(origin: string): boolean {
  return origin === "http://localhost" || /^https?:\/\/localhost:\d+$/.test(origin);
}

export function resolveFrameHostOrigin(frameHostOrigin: string | undefined): string {
  const raw = frameHostOrigin ?? DEFAULT_FRAME_HOST_ORIGIN;

  let normalized: string;
  try {
    normalized = new URL(raw).origin;
  } catch {
    throw new FluzElementsError("INVALID_FRAME_HOST_ORIGIN", `frameHostOrigin is not a valid URL: "${raw}"`);
  }

  const isAllowed =
    ALLOWED_FRAME_HOST_ORIGINS.has(normalized) ||
    (process.env.FLUZ_ALLOW_LOCALHOST_FRAME_HOST === "true" && isLocalhostOrigin(normalized));

  if (!isAllowed) {
    throw new FluzElementsError(
      "INVALID_FRAME_HOST_ORIGIN",
      `frameHostOrigin "${normalized}" is not an allowed Fluz frame host.`,
    );
  }

  return normalized;
}
